// Server functions for Canal de Garimpo — anúncios de vinil com fila.
import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import type { Database } from "@/integrations/supabase/types";

function makeShareCode(): string {
  const chars = "abcdefghjkmnpqrstuvwxyz23456789";
  let s = "";
  for (let i = 0; i < 6; i++) s += chars[Math.floor(Math.random() * chars.length)];
  return s;
}

function normalizeWhatsapp(input: string): string {
  const digits = String(input || "").replace(/\D/g, "");
  if (!digits) return "";
  return digits.startsWith("55") ? digits : `55${digits}`;
}

function createPublicClient() {
  const key = process.env.SUPABASE_PUBLISHABLE_KEY!;
  return createClient<Database>(process.env.SUPABASE_URL!, key, {
    auth: { persistSession: false, autoRefreshToken: false },
    global: {
      fetch: (input, init) => {
        const h = new Headers(init?.headers);
        if (key.startsWith("sb_") && h.get("Authorization") === `Bearer ${key}`) h.delete("Authorization");
        h.set("apikey", key);
        return fetch(input, { ...init, headers: h });
      },
    },
  });
}

const LISTING_COLS =
  "id, share_code, seller_id, artist, title, description, category, discogs_id, cover_url, year, label, price_brl, price_pix_brl, price_card_brl, media_grade, sleeve_grade, pressing, shipping, notes, status, queue_count, views_count, reserved_queue_id, reserved_at, reserved_expires_at, sold_at, sold_to_name, created_at, updated_at";

export const LISTING_CATEGORIES = [
  "Rock",
  "MPB",
  "Samba",
  "Jazz",
  "Blues",
  "Soul / Funk",
  "Hip Hop",
  "Eletrônica",
  "Clássica",
  "Trilha sonora",
  "Internacional",
  "Outros",
] as const;

export type ListingInput = {
  artist: string;
  title: string;
  description?: string | null;
  category?: string | null;
  discogsId?: string | null;
  coverUrl?: string | null;
  year?: number | null;
  label?: string | null;
  priceBrl: number;
  pricePixBrl?: number | null;
  priceCardBrl?: number | null;
  mediaGrade?: string | null;
  sleeveGrade?: string | null;
  pressing?: string | null;
  shipping?: string | null;
  notes?: string | null;
  whatsapp?: string | null;
  photoUrls?: string[];
};

/** Access + contact info for the Canal admin area. */
export const getCanalAccess = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const [{ data: isAdmin }, { data: profile }] = await Promise.all([
      supabase.rpc("has_role", { _user_id: userId, _role: "admin" }),
      supabaseAdmin
        .from("profiles")
        .select("username, display_name, whatsapp")
        .eq("id", userId)
        .maybeSingle(),
    ]);
    return {
      isAdmin: !!isAdmin,
      whatsapp: profile?.whatsapp ?? null,
      username: profile?.username ?? null,
      displayName: profile?.display_name ?? null,
    };
  });

/** Saves the caller's WhatsApp on their profile (one-time collection). */
export const saveMyWhatsapp = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { whatsapp: string }) => input)
  .handler(async ({ data, context }) => {
    const wpp = normalizeWhatsapp(data.whatsapp);
    if (wpp.length < 12) throw new Error("WhatsApp inválido");
    const { error } = await context.supabase
      .from("profiles")
      .update({ whatsapp: wpp })
      .eq("id", context.userId);
    if (error) throw error;
    return { whatsapp: wpp };
  });

export const createListing = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: ListingInput) => input)
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const artist = data.artist.trim();
    const title = data.title.trim();
    if (!artist || !title) throw new Error("Artista e título são obrigatórios");
    if (!data.priceBrl || data.priceBrl <= 0) throw new Error("Preço inválido");

    let wpp = normalizeWhatsapp(data.whatsapp ?? "");
    if (!wpp) {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { data: profile } = await supabaseAdmin
        .from("profiles")
        .select("whatsapp")
        .eq("id", userId)
        .maybeSingle();
      wpp = normalizeWhatsapp(profile?.whatsapp ?? "");
    } else {
      await supabase.from("profiles").update({ whatsapp: wpp }).eq("id", userId);
    }
    if (!wpp || wpp.length < 12) throw new Error("WhatsApp inválido");

    // Try up to 5 times for unique code
    let code = "";
    for (let i = 0; i < 5; i++) {
      code = makeShareCode();
      const { data: exists } = await supabase
        .from("garimpo_listings")
        .select("id")
        .eq("share_code", code)
        .maybeSingle();
      if (!exists) break;
    }

    const { data: listing, error } = await supabase
      .from("garimpo_listings")
      .insert({
        seller_id: userId,
        share_code: code,
        artist,
        title,
        description: data.description?.slice(0, 2000) ?? null,
        category: data.category ?? null,
        discogs_id: data.discogsId ?? null,
        cover_url: data.coverUrl ?? null,
        year: data.year ?? null,
        label: data.label ?? null,
        price_brl: data.priceBrl,
        price_pix_brl: data.pricePixBrl ?? null,
        price_card_brl: data.priceCardBrl ?? null,
        media_grade: data.mediaGrade ?? null,
        sleeve_grade: data.sleeveGrade ?? null,
        pressing: data.pressing ?? null,
        shipping: data.shipping ?? null,
        notes: data.notes?.slice(0, 500) ?? null,
      })
      .select(LISTING_COLS)
      .single();
    if (error) throw error;

    const { error: contactError } = await supabase
      .from("garimpo_listing_contacts")
      .insert({ listing_id: listing.id, seller_id: userId, whatsapp: wpp });
    if (contactError) throw contactError;

    const photos = (data.photoUrls || []).filter(Boolean).slice(0, 8);
    if (photos.length) {
      await supabase
        .from("garimpo_photos")
        .insert(photos.map((url, i) => ({ listing_id: listing.id, url, position: i })));
    }

    return listing;
  });

export const updateListing = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: ListingInput & { id: string }) => input)
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const artist = data.artist.trim();
    const title = data.title.trim();
    if (!artist || !title) throw new Error("Artista e título são obrigatórios");
    if (!data.priceBrl || data.priceBrl <= 0) throw new Error("Preço inválido");

    const { data: owned } = await supabase
      .from("garimpo_listings")
      .select("id")
      .eq("id", data.id)
      .eq("seller_id", userId)
      .maybeSingle();
    if (!owned) throw new Error("Anúncio não encontrado");

    const { data: listing, error } = await supabase
      .from("garimpo_listings")
      .update({
        artist,
        title,
        description: data.description?.slice(0, 2000) ?? null,
        category: data.category ?? null,
        cover_url: data.coverUrl ?? null,
        year: data.year ?? null,
        label: data.label ?? null,
        price_brl: data.priceBrl,
        price_pix_brl: data.pricePixBrl ?? null,
        price_card_brl: data.priceCardBrl ?? null,
        media_grade: data.mediaGrade ?? null,
        sleeve_grade: data.sleeveGrade ?? null,
        pressing: data.pressing ?? null,
        shipping: data.shipping ?? null,
        notes: data.notes?.slice(0, 500) ?? null,
      })
      .eq("id", data.id)
      .eq("seller_id", userId)
      .select(LISTING_COLS)
      .single();
    if (error) throw error;

    const wpp = normalizeWhatsapp(data.whatsapp ?? "");
    if (wpp && wpp.length >= 12) {
      await supabase
        .from("garimpo_listing_contacts")
        .upsert({ listing_id: data.id, seller_id: userId, whatsapp: wpp }, { onConflict: "listing_id" });
    }

    if (data.photoUrls) {
      const photos = data.photoUrls.filter(Boolean).slice(0, 8);
      await supabase.from("garimpo_photos").delete().eq("listing_id", data.id);
      if (photos.length) {
        await supabase
          .from("garimpo_photos")
          .insert(photos.map((url, i) => ({ listing_id: data.id, url, position: i })));
      }
    }

    return listing;
  });

export const deleteListing = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { id: string }) => input)
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { error } = await supabase
      .from("garimpo_listings")
      .delete()
      .eq("id", data.id)
      .eq("seller_id", userId);
    if (error) throw error;
    return { ok: true };
  });


export const listMyListings = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("garimpo_listings")
      .select(LISTING_COLS)
      .eq("seller_id", context.userId)
      .order("created_at", { ascending: false });
    if (error) throw error;
    return data ?? [];
  });

export const getMyListing = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { id: string }) => input)
  .handler(async ({ data, context }) => {
    const { data: listing, error } = await context.supabase
      .from("garimpo_listings")
      .select("id, share_code, seller_id, artist, title, description, category, discogs_id, cover_url, year, label, price_brl, price_pix_brl, price_card_brl, media_grade, sleeve_grade, pressing, shipping, notes, status, queue_count, reserved_queue_id, reserved_at, reserved_expires_at, sold_at, sold_to_name, created_at, updated_at")
      .eq("id", data.id)
      .eq("seller_id", context.userId)
      .maybeSingle();
    if (error) throw error;
    if (!listing) throw new Error("Anúncio não encontrado");
    const [{ data: photos }, { data: queue }] = await Promise.all([
      context.supabase.from("garimpo_photos").select("*").eq("listing_id", data.id).order("position"),
      context.supabase.from("garimpo_queue").select("*").eq("listing_id", data.id).order("position"),
    ]);
    const rows = queue ?? [];
    const userIds = Array.from(new Set(rows.map((r) => r.user_id).filter(Boolean))) as string[];
    let byId = new Map<string, { display_name: string | null; username: string | null; whatsapp: string | null }>();
    if (userIds.length) {
      // Seller ownership already verified above; admin client is needed because
      // profiles.whatsapp is not readable through the public API.
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { data: profs } = await supabaseAdmin
        .from("profiles")
        .select("id, display_name, username, whatsapp")
        .in("id", userIds);
      byId = new Map((profs ?? []).map((p) => [p.id, p]));
    }
    const enriched = rows.map((r) => {
      const p = r.user_id ? byId.get(r.user_id) : undefined;
      return {
        ...r,
        contact_name: r.guest_name || p?.display_name || p?.username || null,
        contact_whatsapp: normalizeWhatsapp(r.guest_whatsapp || p?.whatsapp || "") || null,
      };
    });
    return { listing, photos: photos ?? [], queue: enriched };
  });

export const registerListingView = createServerFn({ method: "POST" })
  .inputValidator((input: { code: string }) => input)
  .handler(async ({ data }) => {
    const code = String(data.code || "").slice(0, 40);
    if (!code) return { ok: false };
    const supabase = createPublicClient();
    await supabase.rpc("increment_listing_views", { _code: code });
    return { ok: true };
  });


export const getPublicListing = createServerFn({ method: "GET" })
  .inputValidator((input: { code: string }) => input)
  .handler(async ({ data }) => {
    const supabase = createPublicClient();
    const { data: listing } = await supabase
      .from("garimpo_listings")
      .select("id, share_code, artist, title, description, category, cover_url, year, label, price_brl, price_pix_brl, price_card_brl, media_grade, sleeve_grade, pressing, shipping, notes, status, queue_count, seller_id, created_at, sold_at")
      .eq("share_code", data.code)
      .maybeSingle();
    if (!listing) return null;
    const [{ data: photos }, { data: seller }] = await Promise.all([
      supabase.from("garimpo_photos").select("url, position").eq("listing_id", listing.id).order("position"),
      supabase.from("profiles").select("username, display_name, avatar_url").eq("id", listing.seller_id).maybeSingle(),
    ]);
    return { listing, photos: photos ?? [], seller };
  });

export const joinQueueAuth = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { code: string }) => input)
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: listing } = await supabase
      .from("garimpo_listings")
      .select("id, status, seller_id")
      .eq("share_code", data.code)
      .maybeSingle();
    if (!listing) throw new Error("Anúncio não encontrado");
    if (listing.status === "sold" || listing.status === "archived") throw new Error("Este anúncio já foi encerrado");
    if (listing.seller_id === userId) throw new Error("Você é o vendedor deste anúncio");

    // RLS on garimpo_queue only exposes the caller's own rows, so the next
    // position must be computed with the admin client to avoid always getting #1.
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: existing } = await supabaseAdmin
      .from("garimpo_queue")
      .select("id, position")
      .eq("listing_id", listing.id)
      .eq("user_id", userId)
      .maybeSingle();
    if (existing) return { position: existing.position, alreadyIn: true };

    const { data: last } = await supabaseAdmin
      .from("garimpo_queue")
      .select("position")
      .eq("listing_id", listing.id)
      .order("position", { ascending: false })
      .limit(1)
      .maybeSingle();
    const nextPos = (last?.position ?? 0) + 1;

    const { error } = await supabaseAdmin.from("garimpo_queue").insert({
      listing_id: listing.id,
      user_id: userId,
      position: nextPos,
    });
    if (error) throw error;
    return { position: nextPos, alreadyIn: false };
  });

export const joinQueueGuest = createServerFn({ method: "POST" })
  .inputValidator((input: { code: string; name: string; whatsapp: string }) => input)
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const name = data.name.trim().slice(0, 80);
    const wpp = normalizeWhatsapp(data.whatsapp);
    if (name.length < 2) throw new Error("Nome muito curto");
    if (wpp.length < 12) throw new Error("WhatsApp inválido");

    const { data: listing } = await supabaseAdmin
      .from("garimpo_listings")
      .select("id, status")
      .eq("share_code", data.code)
      .maybeSingle();
    if (!listing) throw new Error("Anúncio não encontrado");
    if (listing.status === "sold" || listing.status === "archived") throw new Error("Anúncio encerrado");

    const { data: existing } = await supabaseAdmin
      .from("garimpo_queue")
      .select("id, position")
      .eq("listing_id", listing.id)
      .eq("guest_whatsapp", wpp)
      .maybeSingle();
    if (existing) return { position: existing.position, alreadyIn: true };

    const { data: last } = await supabaseAdmin
      .from("garimpo_queue")
      .select("position")
      .eq("listing_id", listing.id)
      .order("position", { ascending: false })
      .limit(1)
      .maybeSingle();
    const nextPos = (last?.position ?? 0) + 1;

    const { error } = await supabaseAdmin.from("garimpo_queue").insert({
      listing_id: listing.id,
      guest_name: name,
      guest_whatsapp: wpp,
      position: nextPos,
    });
    if (error) throw error;
    return { position: nextPos, alreadyIn: false };
  });

/** Ranking público (anonimizado) — usado só depois que a pessoa entra na fila. */
export const getPublicQueue = createServerFn({ method: "GET" })
  .inputValidator((input: { code: string }) => input)
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: listing } = await supabaseAdmin
      .from("garimpo_listings")
      .select("id")
      .eq("share_code", data.code)
      .maybeSingle();
    if (!listing) return [];
    const { data: rows } = await supabaseAdmin
      .from("garimpo_queue")
      .select("id, position, status, guest_name, user_id")
      .eq("listing_id", listing.id)
      .in("status", ["waiting", "reserved"])
      .order("position");
    const userIds = (rows ?? []).map((r) => r.user_id).filter(Boolean) as string[];
    const names = new Map<string, string>();
    if (userIds.length) {
      const { data: profiles } = await supabaseAdmin
        .from("profiles")
        .select("id, display_name, username")
        .in("id", userIds);
      for (const p of profiles ?? []) names.set(p.id, p.display_name || p.username || "Colecionador");
    }
    return (rows ?? []).map((r, i) => {
      const full = r.user_id ? names.get(r.user_id) || "Colecionador" : r.guest_name || "Visitante";
      const parts = full.trim().split(/\s+/);
      const masked = parts[0] + (parts[1] ? ` ${parts[1][0].toUpperCase()}.` : "");
      return { id: r.id, rank: i + 1, name: masked, status: r.status };
    });
  });


export const reserveInterested = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { listingId: string; queueId: string }) => input)
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: listing } = await supabase
      .from("garimpo_listings")
      .select("id, seller_id")
      .eq("id", data.listingId)
      .maybeSingle();
    if (!listing || listing.seller_id !== userId) throw new Error("Sem permissão");
    const expires = new Date(Date.now() + 24 * 3600 * 1000).toISOString();
    await supabase.from("garimpo_queue").update({ status: "reserved" }).eq("id", data.queueId);
    await supabase
      .from("garimpo_listings")
      .update({
        status: "reserved",
        reserved_queue_id: data.queueId,
        reserved_at: new Date().toISOString(),
        reserved_expires_at: expires,
      })
      .eq("id", data.listingId);
    return { ok: true };
  });

export const skipInterested = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { listingId: string; queueId: string }) => input)
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: listing } = await supabase
      .from("garimpo_listings")
      .select("id, seller_id, reserved_queue_id")
      .eq("id", data.listingId)
      .maybeSingle();
    if (!listing || listing.seller_id !== userId) throw new Error("Sem permissão");
    await supabase.from("garimpo_queue").update({ status: "skipped" }).eq("id", data.queueId);
    if (listing.reserved_queue_id === data.queueId) {
      await supabase
        .from("garimpo_listings")
        .update({ status: "available", reserved_queue_id: null, reserved_at: null, reserved_expires_at: null })
        .eq("id", data.listingId);
    }
    return { ok: true };
  });

export const markSold = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { listingId: string; queueId?: string; buyerName?: string }) => input)
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: listing } = await supabase
      .from("garimpo_listings")
      .select("id, seller_id")
      .eq("id", data.listingId)
      .maybeSingle();
    if (!listing || listing.seller_id !== userId) throw new Error("Sem permissão");
    let buyerName = data.buyerName ?? null;
    if (data.queueId) {
      const { data: q } = await supabase
        .from("garimpo_queue")
        .select("guest_name, user_id")
        .eq("id", data.queueId)
        .maybeSingle();
      if (q) {
        if (!buyerName && q.guest_name) buyerName = q.guest_name;
        if (!buyerName && q.user_id) {
          const { data: p } = await supabase
            .from("profiles")
            .select("display_name, username")
            .eq("id", q.user_id)
            .maybeSingle();
          buyerName = p?.display_name || p?.username || null;
        }
      }
      await supabase.from("garimpo_queue").update({ status: "sold" }).eq("id", data.queueId);
    }
    await supabase
      .from("garimpo_listings")
      .update({ status: "sold", sold_at: new Date().toISOString(), sold_to_name: buyerName })
      .eq("id", data.listingId);
    return { ok: true };
  });

export const archiveListing = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { listingId: string }) => input)
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("garimpo_listings")
      .update({ status: "archived" })
      .eq("id", data.listingId)
      .eq("seller_id", context.userId);
    if (error) throw error;
    return { ok: true };
  });

export const setListingStatus = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    (input: { listingId: string; status: "available" | "reserved" | "sold" | "archived" }) => input,
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const patch = {
      status: data.status,
      ...(data.status === "sold" ? { sold_at: new Date().toISOString() } : {}),
      ...(data.status === "available"
        ? {
            reserved_queue_id: null,
            reserved_at: null,
            reserved_expires_at: null,
            sold_at: null,
          }
        : {}),
    };
    const { error } = await supabase
      .from("garimpo_listings")
      .update(patch)
      .eq("id", data.listingId)
      .eq("seller_id", userId);
    if (error) throw error;
    return { ok: true };
  });

export const listSellerAvailable = createServerFn({ method: "GET" })
  .inputValidator((input: { username: string }) => input)
  .handler(async ({ data }) => {
    const supabase = createPublicClient();
    const { data: profile } = await supabase
      .from("profiles")
      .select("id, username, display_name, avatar_url, city, country")
      .eq("username", data.username)
      .maybeSingle();
    if (!profile) return null;
    const { data: listings } = await supabase
      .from("garimpo_listings")
      .select("id, share_code, artist, title, description, category, cover_url, price_brl, price_pix_brl, price_card_brl, media_grade, sleeve_grade, status, queue_count, created_at")
      .eq("seller_id", profile.id)
      .in("status", ["available", "reserved"])
      .order("created_at", { ascending: false });
    return { profile, listings: listings ?? [] };
  });

/** Aggregate metrics for the Canal admin dashboard. */
export const getCanalStats = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data, error } = await supabase
      .from("garimpo_listings")
      .select("id, status, price_brl, views_count, queue_count, sold_at")
      .eq("seller_id", userId);
    if (error) throw error;
    const rows = data ?? [];
    const sold = rows.filter((r) => r.status === "sold");
    return {
      total: rows.length,
      available: rows.filter((r) => r.status === "available").length,
      reserved: rows.filter((r) => r.status === "reserved").length,
      sold: sold.length,
      views: rows.reduce((a, r) => a + (r.views_count ?? 0), 0),
      interested: rows.reduce((a, r) => a + (r.queue_count ?? 0), 0),
      revenue: sold.reduce((a, r) => a + Number(r.price_brl ?? 0), 0),
      inventoryValue: rows
        .filter((r) => r.status === "available" || r.status === "reserved")
        .reduce((a, r) => a + Number(r.price_brl ?? 0), 0),
    };
  });
